import React, { useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from './contexts/AuthContext';
import type { AccessibleTenant } from './api/types/auth.types';
import { filterNavigationMenu, applyRoleActions, ROLE_PRIORITY } from './config/rolePermissions';
import { rolePermissionService } from './api/services/rolePermissionService';
import { tokenManager } from './auth/tokenManager';

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginProps {
  onLoginSuccess?: (user: any) => void;
}

type AuthStep = 'credentials' | 'tenant-select';

// ============================================================================
// APP CONFIG - Application settings (title, etc.)
// Defined at module level so it can be used for both display and localStorage
// ============================================================================
const appConfig = {
  appTitle: 'PRIMA'  // This will appear in header (top-left), login page, and welcome message
};

// ============================================================================
// ALL PRIMA NAVIGATION MENU — full menu (filtered by role at login time)
// Icons must match the IconSystem keys used in main_layout.tsx
// ============================================================================
const ALL_NAVIGATION_MENU = [
  {
    section: 'Dashboard',
    icon: 'layout',
    items: [
      { id: 'overview',          title: 'Overview',          icon: 'home',      active: true },
      { id: 'manager-dashboard', title: 'Manager Dashboard', icon: 'dashboard', active: true },
    ],
  },
  {
    section: 'Masters',
    icon: 'folder',
    items: [
      { id: 'engagement-type-master', title: 'Engagement Type', icon: 'tag',      active: true },
      { id: 'activity-master',        title: 'Activity',         icon: 'file',     active: true },
      { id: 'client-type-master',     title: 'Client Type',      icon: 'tag',      active: true },
      { id: 'designation-master',     title: 'Designation',      icon: 'user',     active: true },
      { id: 'location-master',        title: 'Location',         icon: 'home',     active: true },
      { id: 'client-group-master',    title: 'Client Group',     icon: 'grid',     active: true },
      { id: 'employee-master',        title: 'Employee',         icon: 'user',     active: true },
      { id: 'holiday-master',         title: 'Holiday',          icon: 'calendar', active: true },
      { id: 'client-master',          title: 'Client',           icon: 'file',     active: true },
    ],
  },
  {
    section: 'Transactions',
    icon: 'file',
    items: [
      { id: 'engagement-management', title: 'Engagement',   icon: 'file',       active: true },
      { id: 'disbursement-entry',    title: 'Disbursement', icon: 'dollarSign', active: true },
      { id: 'timesheet-entry',       title: 'Timesheet',    icon: 'clock',      active: true },
    ],
  },
  {
    section: 'Reports',
    icon: 'chart',
    items: [
      { id: 'employee-activity-report', title: 'Employee Activity', icon: 'chart',      active: true },
      { id: 'wip-report',               title: 'WIP Report',        icon: 'dollarSign', active: true },
      { id: 'timesheet-status-report',  title: 'Timesheet Status',  icon: 'clock',      active: true },
    ],
  },
  {
    section: 'FSA',
    icon: 'chart',
    items: [
      { id: 'fsa-engagements', title: 'Engagements',     icon: 'briefcase', active: true },
      { id: 'fsa-templates',   title: 'Template Browser', icon: 'chart',     active: true },
      { id: 'fsa-audit-firms', title: 'Audit Firms',     icon: 'users',     active: true },
      { id: 'fsa-audit-partners', title: 'Audit Partners', icon: 'user',     active: true },
      { id: 'fsa-director-signatories', title: 'Directors / Signatories', icon: 'users', active: true },
      { id: 'fsa-partners', title: 'Partners', icon: 'users', active: true },
    ],
  },
  {
    section: 'Administration',
    icon: 'lock',
    items: [
      { id: 'role-permissions', title: 'Role Permissions', icon: 'lock',     active: true },
      { id: 'config-settings',  title: 'Configuration',    icon: 'settings', active: true },
      { id: 'fsa-document-output', title: 'Document Output Settings', icon: 'settings', active: true },
    ],
  },
];

// ============================================================================
// ALL PRIMA MODULES CONFIG — full map (filtered by role at login time)
// componentName keys must match COMPONENT_REGISTRY entries
// ============================================================================
const ALL_MODULES_CONFIG: Record<string, any> = {
  'overview': {
    showHeader: false,
    title: 'Overview',
    description: 'Welcome to PRIMA',
    componentName: 'OverviewScreen',
    actions: ['view'],
    permissions: ['admin', 'manager', 'partner', 'employee'],
    breadcrumb: ['Dashboard', 'Overview'],
  },
  'engagement-type-master': {
    showHeader: false,
    title: 'Engagement Type',
    description: 'Manage engagement type master records',
    componentName: 'EngagementTypeMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Engagement Type'],
  },
  'activity-master': {
    showHeader: false,
    title: 'Activity',
    description: 'Manage activity master records',
    componentName: 'ActivityMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Activity'],
  },
  'client-type-master': {
    showHeader: false,
    title: 'Client Type',
    description: 'Manage client type master records',
    componentName: 'ClientTypeMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Client Type'],
  },
  'designation-master': {
    showHeader: false,
    title: 'Designation',
    description: 'Manage designation master records',
    componentName: 'DesignationMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Designation'],
  },
  'location-master': {
    showHeader: false,
    title: 'Location',
    description: 'Manage office / branch location records',
    componentName: 'OfficeMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Location'],
  },
  'client-group-master': {
    showHeader: false,
    title: 'Client Group',
    description: 'Manage client group master records',
    componentName: 'ClientGroupMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Client Group'],
  },
  'employee-master': {
    showHeader: false,
    title: 'Employee',
    description: 'Manage employee master records',
    componentName: 'EmployeeMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Employee'],
  },
  'holiday-master': {
    showHeader: false,
    title: 'Holiday',
    description: 'Manage public holiday records',
    componentName: 'HolidayMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Holiday'],
  },
  'client-master': {
    showHeader: false,
    title: 'Client',
    description: 'Manage client records',
    componentName: 'ClientMasterScreen',
    actions: ['add', 'edit', 'delete'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Masters', 'Client'],
  },
  'engagement-management': {
    showHeader: false,
    title: 'Engagement',
    description: 'Manage client engagements',
    componentName: 'EngagementMasterScreen',
    actions: ['add', 'edit', 'delete', 'view', 'view-all-employees', 'view-own-team', 'manage-all-employees', 'manage-own-team', 'close', 'reopen'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Transactions', 'Engagement'],
  },
  'disbursement-entry': {
    showHeader: false,
    title: 'Disbursement',
    description: 'Record disbursement entries',
    componentName: 'DisbursementMasterScreen',
    actions: ['add', 'edit', 'delete', 'view', 'view-all-employees', 'view-own-team', 'manage-all-employees', 'manage-own-team'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Transactions', 'Disbursement'],
  },
  'timesheet-entry': {
    showHeader: false,
    title: 'Timesheet Entry',
    description: 'Weekly timesheet entry and submission',
    componentName: 'TimesheetMasterScreen',
    actions: ['add', 'edit', 'delete', 'view', 'view-all-employees', 'view-own-team', 'manage-all-employees', 'manage-own-team', 'reopen'],
    permissions: ['admin', 'manager', 'supervisor', 'user'],
    breadcrumb: ['Transactions', 'Timesheet'],
  },
  'employee-activity-report': {
    showHeader: false,
    title: 'Employee Activity Report',
    description: 'Detailed activity log per employee',
    componentName: 'EmployeeActivityReportScreen',
    actions: ['view', 'export', 'view-all-employees', 'view-own-team'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Reports', 'Employee Activity'],
  },
  'wip-report': {
    showHeader: false,
    title: 'WIP Report',
    description: 'Work-in-progress billing summary',
    componentName: 'WipReportScreen',
    actions: ['view', 'export', 'view-all-employees', 'view-own-team'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Reports', 'WIP Report'],
  },
  'timesheet-status-report': {
    showHeader: false,
    title: 'Timesheet Status Report',
    description: 'Weekly timesheet submission status per employee',
    componentName: 'TimesheetStatusReportScreen',
    actions: ['view', 'export', 'view-all-employees', 'view-own-team'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Reports', 'Timesheet Status'],
  },
  'manager-dashboard': {
    showHeader: false,
    title: 'Manager Dashboard',
    description: 'Team overview with widgets for pending timesheets, engagement status, and WIP summary',
    componentName: 'ManagerDashboardScreen',
    actions: ['view', 'view-all-employees', 'view-own-team'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['Dashboard', 'Manager Dashboard'],
  },
  'fsa-engagements': {
    showHeader: false,
    title: 'FSA Engagements',
    description: 'Engagements with FSA configured — open one to enter its workspace',
    componentName: 'FsaEngagementListScreen',
    actions: ['view'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['FSA', 'Engagements'],
  },
  'fsa-templates': {
    showHeader: false,
    title: 'FSA Template Browser',
    description: 'Read-only browser of system-seeded financial-statement templates',
    componentName: 'FsaTemplateMasterScreen',
    actions: ['view'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['FSA', 'Template Browser'],
  },
  'fsa-audit-firms': {
    showHeader: false,
    title: 'Audit Firms',
    description: 'Firm-wide master of auditing firms. Drives the auditor side of signed Financial Statements.',
    componentName: 'FsaAuditFirmMasterScreen',
    actions: ['add', 'edit', 'delete', 'view'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['FSA', 'Audit Firms'],
  },
  'fsa-audit-partners': {
    showHeader: false,
    title: 'Audit Partners',
    description: 'Per-firm signing partner roster. Used by the Engagement Signature Panel to pick which partners sign.',
    componentName: 'FsaAuditPartnerMasterScreen',
    actions: ['add', 'edit', 'delete', 'view'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['FSA', 'Audit Partners'],
  },
  'fsa-director-signatories': {
    showHeader: false,
    title: 'Directors / Signatories',
    description: 'Per-client roster of directors and officers. Used by the Engagement Signature Panel to pick board-side signatories.',
    componentName: 'FsaDirectorSignatoryMasterScreen',
    actions: ['add', 'edit', 'delete', 'view'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['FSA', 'Directors / Signatories'],
  },
  'fsa-partners': {
    showHeader: false,
    title: 'Partners',
    description: 'Per-firm roster of partners (PSR + signatory) for partnership-firm engagements.',
    componentName: 'FsaPartnerMasterScreen',
    actions: ['add', 'edit', 'delete', 'view'],
    permissions: ['admin', 'manager', 'supervisor'],
    breadcrumb: ['FSA', 'Partners'],
  },
  'role-permissions': {
    showHeader: false,
    title: 'Role Permissions',
    description: 'Configure module visibility and action permissions for each Keycloak role',
    componentName: 'RolePermissionScreen',
    actions: ['add', 'edit', 'delete', 'view'],
    permissions: ['admin'],
    breadcrumb: ['Administration', 'Role Permissions'],
  },
  'config-settings': {
    showHeader: false,
    title: 'Configuration Settings',
    description: 'Manage timesheet, engagement, and system policy settings with cascade scope (System / Office / Employee)',
    componentName: 'ConfigSettingsScreen',
    actions: ['edit', 'view'],
    permissions: ['admin'],
    breadcrumb: ['Administration', 'Configuration Settings'],
  },
  'fsa-document-output': {
    showHeader: false,
    title: 'Document Output Settings',
    description: 'Control how FSA financial-statement PDFs are generated — header, page layout, font, footer, and signature wording',
    componentName: 'FsaDocumentOutputSettingsScreen',
    actions: ['edit', 'view'],
    permissions: ['admin'],
    breadcrumb: ['Administration', 'Document Output Settings'],
  },
};

const getAuthMode = (): 'tenant-hub' | 'legacy' => {
  // Fail-secure: ONLY an explicit REACT_APP_AUTH_MODE=legacy enables the mock
  // login. Unset, unknown, or any other value resolves to tenant-hub (real
  // Keycloak/TenantHub auth) — the app never falls open to the mock login.
  return process.env.REACT_APP_AUTH_MODE === 'legacy' ? 'legacy' : 'tenant-hub';
};

const LoginInterface: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [isPending, startTransition] = useTransition();
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  // Tenant-Hub auth flow state
  const [authStep, setAuthStep] = useState<AuthStep>('credentials');
  const [accessibleTenants, setAccessibleTenants] = useState<AccessibleTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedAppId, setSelectedAppId] = useState<string>('');

  const authContext = useAuthContext();
  const navigate = useNavigate();

  // Real-time email validation (only checks if not empty)
  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('User ID is required');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Real-time password validation
  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return false;
    }
    setPasswordError('');
    return true;
  };

  // Handle tenant-hub discover step
  const handleTenantHubDiscover = async () => {
    setError('');
    const isEmailValid = validateEmail(credentials.email);
    const isPasswordValid = validatePassword(credentials.password);
    if (!isEmailValid || !isPasswordValid) return;

    // PRIMA is one specific application. Its app id MUST be configured, and the
    // signed-in user MUST have access to exactly this app — otherwise login is
    // rejected. The app is never inferred from whatever apps the user happens
    // to have; an invalid or unauthorized REACT_APP_APPLICATION_ID fails login.
    const requiredAppId = process.env.REACT_APP_APPLICATION_ID?.trim();
    if (!requiredAppId) {
      setError('Application is not configured. Set REACT_APP_APPLICATION_ID in your environment.');
      return;
    }

    startTransition(async () => {
      try {
        const discoveryResponse = await authContext.discover(
          credentials.email,
          credentials.password,
          requiredAppId // sent as clientId to filter apps
        );

        const tenants = discoveryResponse.accessibleTenants;

        if (tenants.length === 0) {
          setError('No accessible tenants found for your account.');
          return;
        }

        // Only tenants that actually grant access to the configured PRIMA app
        // are valid. Zero candidates ⇒ the app id is invalid or the user isn't
        // authorized for it ⇒ reject login (do NOT fall back to another app).
        const candidates = tenants.filter((t) =>
          t.applications.some((a) => a.applicationId === requiredAppId)
        );
        if (candidates.length === 0) {
          setError('You are not authorized to access this application.');
          return;
        }

        // Single candidate → authenticate immediately with the fixed app.
        if (candidates.length === 1) {
          await handleTenantHubAuthenticate(candidates[0].tenantId, requiredAppId);
          return;
        }

        // Multiple tenants grant the app → user picks the tenant; the app is
        // fixed to the configured PRIMA app (no application picker).
        const recommended = discoveryResponse.recommendedTenant;
        const preselect =
          (recommended && candidates.find((t) => t.tenantId === recommended.tenantId)) ||
          candidates[0];
        setAccessibleTenants(candidates);
        setSelectedTenantId(preselect.tenantId);
        setSelectedAppId(requiredAppId);
        setAuthStep('tenant-select');
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401) {
          setError('Invalid username or password. Please try again.');
        } else {
          setError(err?.response?.data?.message || err.message || 'Discovery failed. Please check your credentials.');
        }
      }
    });
  };

  // Handle tenant-hub authenticate step
  const handleTenantHubAuthenticate = async (tenantId?: string, appId?: string) => {
    const tId = tenantId || selectedTenantId;
    const aId = appId || selectedAppId;

    if (!tId || !aId) {
      setError('Please select a tenant and application.');
      return;
    }

    startTransition(async () => {
      try {
        await authContext.authenticate(
          credentials.email,
          credentials.password,
          tId,
          aId
        );

        // ---------------------------------------------------------------
        // Extract PRIMA roles from the JWT
        // ---------------------------------------------------------------
        let primaRoles: string[] = [];
        try {
          const token = tokenManager.getAccessToken();
          if (token) {
            const payload = tokenManager.decodeToken(token);
            const resourceRoles: string[] = (payload as any)?.resource_access?.prima?.roles ?? [];
            primaRoles = ROLE_PRIORITY.filter((r) => resourceRoles.includes(r));
          }
        } catch {
          // If JWT parsing fails, primaRoles stays empty → login blocked below
        }

        // ---------------------------------------------------------------
        // Fetch role permissions from DB — always called so that
        // UserCacheMiddleware auto-registers the user in tbl_user_cache,
        // even when the user has no PRIMA roles assigned yet.
        // ---------------------------------------------------------------
        const roleForLookup = primaRoles.length > 0 ? primaRoles[0] : 'prima_none';
        let dbPermissions: { visibleModules: string[]; moduleActions: Record<string, string[]> } | null = null;
        try {
          const result = await rolePermissionService.getByRoles(roleForLookup);
          if (result.success && result.data && result.data.visibleModules.length > 0) {
            dbPermissions = result.data;
          } else {
            // Request succeeded but returned no permissions for this role.
            // Surface it (do NOT swallow) so misconfiguration is diagnosable.
            console.error('[login] getByRoles returned no permissions', { role: roleForLookup, response: result });
          }
        } catch (e) {
          // Surface the real failure (status/headers/network) instead of hiding it.
          console.error('[login] getByRoles request failed', { role: roleForLookup, error: e });
        }

        // Check PRIMA roles AFTER the by-roles call so the middleware
        // side-effect (user registration) always happens first.
        if (primaRoles.length === 0) {
          setError('No PRIMA roles are assigned to your account. Contact your administrator.');
          authContext.logout();
          return;
        }

        // No client-side fallback: permissions come exclusively from the DB
        // (admin.tbl_role_permissions via /by-roles). If none are configured —
        // even for prima_system — login is rejected. Recovery is via re-running
        // the role-permissions seed, never a hardcoded bootstrap grant.
        if (!dbPermissions) {
          setError('Role permissions are not configured for your role. Contact your administrator.');
          authContext.logout();
          return;
        }

        // ---------------------------------------------------------------
        // Build filtered navigation and modules for this role
        // ---------------------------------------------------------------
        const filteredNav = filterNavigationMenu(ALL_NAVIGATION_MENU, dbPermissions.visibleModules);
        const filteredModules = applyRoleActions(ALL_MODULES_CONFIG, dbPermissions);

        localStorage.setItem('NAVIGATION_MENU', JSON.stringify(filteredNav));
        localStorage.setItem('MODULES_CONFIG', JSON.stringify(filteredModules));
        localStorage.setItem('APP_CONFIG', JSON.stringify(appConfig));

        navigate('../Layout/main_layout');
        onLoginSuccess?.({ username: credentials.email });
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401) {
          setError('Invalid username or password. Please try again.');
        } else {
          setError(err?.response?.data?.message || err.message || 'Authentication failed. Please try again.');
        }
      }
    });
  };

  const handleSubmit = () => {
    setError('');

    // Validate both fields
    const isEmailValid = validateEmail(credentials.email);
    const isPasswordValid = validatePassword(credentials.password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    // === Tenant-Hub Mode ===
    if (getAuthMode() === 'tenant-hub') {
      if (authStep === 'credentials') {
        handleTenantHubDiscover();
      } else {
        handleTenantHubAuthenticate();
      }
      return;
    }

    // === Legacy Mode (unchanged) ===
    startTransition(async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Mock successful login
        if (credentials.email && credentials.password) {
          const mockUser = {
            id: 1,
            name: 'Admin',
            role: 'Administrator',
            email: credentials.email,
            avatar: 'AK',
            permissions: ['read', 'write', 'admin']
          };

          localStorage.setItem('authToken', 'mock-jwt-token');
          localStorage.setItem('user', JSON.stringify(mockUser));

          // ============================================================================
          // NAVIGATION MENU - Define sidebar menu structure
          // ============================================================================
          const navigationMenu = [
            {
              "section": "Dashboard",
              "icon": "layout",
              "items": [
                { "id": "overview", "title": "Overview", "icon": "home", "active": true },
                { "id": "analytics", "title": "Analytics", "icon": "chart" }
              ]
            },
            {
              "section": "Sample",
              "icon": "folder",
              "items": [
                {
                  "id": "sample-module-master-tabbed",
                  "title": "Sample Screen (Tabbed)",
                  "icon": "settings",
                },
                {
                  "id": "sample-module-master",
                  "title": "Sample Screen",
                  "icon": "settings",
                },
              ]
            },
          ];

          // ============================================================================
          // MODULES CONFIG - Define component mapping for each menu item
          // ============================================================================
          const modulesConfig = {
            'sample-module-master-tabbed': {
              showHeader: false,
              title: 'Sample Module Master',
              description: 'Manage sample master records',
              componentName: 'SampleMasterScreenTabbed',
              actions: ['add', 'edit', 'delete', 'export'],
              permissions: ['admin', 'manager', 'supervisor'],
              breadcrumb: ['Sample Module', 'Sample Module Master']
            },
            'sample-module-master': {
              showHeader: false,
              title: 'Sample Module Master',
              description: 'Manage sample master records',
              componentName: 'SampleMasterScreen',
              actions: ['add', 'edit', 'delete', 'export'],
              permissions: ['admin', 'manager', 'supervisor'],
              breadcrumb: ['Sample Module', 'Sample Module Master']
            },
            'user-management': {
              showHeader: true,
              title: 'User Management',
              description: 'Manage system users and their access',
              componentName: '',
              actions: ['add', 'edit', 'delete'],
              permissions: ['admin'],
              breadcrumb: ['Administration', 'User Management']
            },
            'role-management': {
              showHeader: true,
              title: 'Role Management',
              description: 'Manage user roles and permissions',
              componentName: '',
              actions: ['add', 'edit', 'delete'],
              permissions: ['admin'],
              breadcrumb: ['Administration', 'Role Management']
            },
            'audit-logs': {
              showHeader: true,
              title: 'Audit Logs',
              description: 'View system audit trail',
              componentName: '',
              actions: ['view', 'export'],
              permissions: ['admin', 'auditor'],
              breadcrumb: ['Administration', 'Audit Logs']
            }
          };

          localStorage.setItem('NAVIGATION_MENU', JSON.stringify(navigationMenu));
          localStorage.setItem('MODULES_CONFIG', JSON.stringify(modulesConfig));
          localStorage.setItem('APP_CONFIG', JSON.stringify(appConfig));

          navigate('../Layout/main_layout');

          onLoginSuccess?.(mockUser);
        } else {
          setError('Please enter valid credentials');
        }
      } catch (err) {
        setError('Login failed. Please try again.');
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));

    // Real-time validation
    if (name === 'email') {
      validateEmail(value);
    } else if (name === 'password') {
      validatePassword(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="lg-root">
      <style>{`
        .lg-root {
          display: flex;
          height: 100vh;
          overflow: hidden;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", Roboto, sans-serif;
          --lg-dark: #0f172a;
          --lg-mid: #1e293b;
          --lg-teal: #14b8a6;
          --lg-teal-focus: rgba(20, 184, 166, 0.15);
          --lg-teal-ring: rgba(20, 184, 166, 0.25);
          --lg-slate-50: #f8fafc;
          --lg-slate-100: #f1f5f9;
          --lg-slate-200: #e2e8f0;
          --lg-slate-400: #94a3b8;
          --lg-slate-500: #64748b;
          --lg-slate-700: #334155;
          --lg-slate-900: #0f172a;
          --lg-danger: #ef4444;
          --lg-danger-bg: #fef2f2;
          --lg-danger-border: #fecaca;
        }

        .lg-root * {
          box-sizing: border-box;
        }

        /* ── BRAND PANEL ─────────────────────────────────────────────── */
        .lg-brand {
          width: 44%;
          flex-shrink: 0;
          background: linear-gradient(155deg, var(--lg-dark) 0%, var(--lg-mid) 100%);
          display: flex;
          flex-direction: column;
          padding: 1.5rem 2.5rem;
          position: relative;
          overflow-y: auto;
          overflow-x: hidden;
          box-shadow: 8px 0 40px rgba(0, 0, 0, 0.3);
          z-index: 1;
        }

        .lg-brand-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          flex-shrink: 0;
          z-index: 1;
        }

        .lg-brand-logo-img {
          height: 88px;
          width: auto;
          max-width: 440px;
          object-fit: contain;
          display: block;
          /* syut_logo.png is dark teal — invert to pure white so it stands
             out on the dark sidebar gradient. brightness(0) flattens the
             image to solid black, then invert(1) flips black → white. */
          filter: brightness(0) invert(1);
        }

        .lg-graphic {
          flex: 1;
          min-height: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.75rem 0;
          overflow: hidden;
          z-index: 1;
        }

        .lg-graphic svg {
          width: 100%;
          max-width: 280px;
          height: auto;
          flex-shrink: 1;
          min-height: 0;
        }

        .lg-hub-ring   { animation: lg-ring-pulse 4s ease-in-out infinite; }
        .lg-hub-ring-2 { animation: lg-ring-pulse 4s ease-in-out infinite 1.5s; }
        .lg-hub-ring-3 { animation: lg-ring-pulse 4s ease-in-out infinite 0.8s; }
        .lg-hub-ring-4 { animation: lg-ring-pulse 4s ease-in-out infinite 2.2s; }
        .lg-flow-a { stroke-dasharray: 6 10; animation: lg-flow 2.4s linear infinite; }
        .lg-flow-b { stroke-dasharray: 6 10; animation: lg-flow 3.1s linear infinite 0.9s; }
        .lg-flow-c { stroke-dasharray: 6 10; animation: lg-flow-rev 2.8s linear infinite 0.4s; }

        /* ── STATS STRIP ─────────────────────────────────────────────── */
        .lg-stats {
          display: flex;
          align-items: stretch;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 0.75rem;
          margin-bottom: 1rem;
          flex-shrink: 0;
          z-index: 1;
          overflow: hidden;
        }

        .lg-stat-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.875rem 0.5rem;
          gap: 0.25rem;
        }

        .lg-stat-item + .lg-stat-item {
          border-left: 1px solid rgba(255, 255, 255, 0.08);
        }

        .lg-stat-value {
          font-size: 1.375rem;
          font-weight: 800;
          color: var(--lg-teal);
          letter-spacing: -0.03em;
          line-height: 1;
          white-space: nowrap;
        }

        .lg-stat-label {
          font-size: 0.5625rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: center;
        }

        /* ── FEATURE CARDS ───────────────────────────────────────────── */
        .lg-features {
          z-index: 1;
          flex-shrink: 0;
          margin-bottom: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .lg-feature-item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.07);
          border-radius: 0.625rem;
          padding: 0.625rem 0.75rem;
        }

        .lg-feature-icon {
          width: 2rem;
          height: 2rem;
          border-radius: 0.5rem;
          background: rgba(20, 184, 166, 0.12);
          border: 1px solid rgba(20, 184, 166, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--lg-teal);
        }

        .lg-feature-text {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          min-width: 0;
        }

        .lg-feature-title {
          font-size: 0.8125rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.82);
          letter-spacing: 0.005em;
          line-height: 1.3;
        }

        .lg-feature-desc {
          font-size: 0.6875rem;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.35);
          letter-spacing: 0.005em;
          line-height: 1.4;
        }

        /* ── APP IDENTITY ────────────────────────────────────────────── */
        .lg-app-identity {
          margin-bottom: 1.875rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid var(--lg-slate-200);
        }

        .lg-app-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          background: rgba(20, 184, 166, 0.1);
          border: 1px solid rgba(20, 184, 166, 0.22);
          border-radius: 2rem;
          padding: 0.2rem 0.625rem;
          font-size: 0.625rem;
          font-weight: 700;
          color: #0d9488;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .lg-app-badge-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #14b8a6;
          flex-shrink: 0;
        }

        .lg-app-name {
          font-size: 1.3125rem;
          font-weight: 800;
          color: var(--lg-slate-900);
          letter-spacing: -0.03em;
          line-height: 1.2;
          margin: 0 0 0.5rem;
        }

        /* ── FORM PANEL ──────────────────────────────────────────────── */
        .lg-form-panel {
          flex: 1;
          background: #fafbff;
          background-image: radial-gradient(circle, rgba(148, 163, 184, 0.2) 1px, transparent 1px);
          background-size: 22px 22px;
          display: flex;
          align-items: flex-start;
          justify-content: center;
          padding: 2.5rem 2rem;
          overflow-y: auto;
        }

        .lg-form-inner {
          width: 100%;
          max-width: 400px;
          margin: auto 0;
          animation: lg-enter 0.55s cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .lg-form-h1 {
          font-size: 1.625rem;
          font-weight: 700;
          color: var(--lg-slate-900);
          letter-spacing: -0.035em;
          margin: 0 0 0.375rem;
          line-height: 1.2;
        }

        .lg-form-sub {
          font-size: 0.875rem;
          color: var(--lg-slate-500);
          margin: 0 0 2rem;
          font-weight: 400;
          line-height: 1.5;
        }

        /* ── FIELDS ──────────────────────────────────────────────────── */
        .lg-field { margin-bottom: 1.125rem; }

        .lg-label {
          display: block;
          font-size: 0.6875rem;
          font-weight: 700;
          color: var(--lg-slate-700);
          margin-bottom: 0.4rem;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .lg-label-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.4rem;
        }

        .lg-label-row .lg-label { margin-bottom: 0; }

        .lg-input-wrap { position: relative; }

        .lg-input-icon {
          position: absolute;
          left: 0.875rem;
          top: 50%;
          transform: translateY(-50%);
          color: var(--lg-slate-400);
          display: flex;
          align-items: center;
          pointer-events: none;
          transition: color 0.2s;
        }

        .lg-input {
          width: 100%;
          padding: 0.6875rem 0.875rem 0.6875rem 2.625rem;
          font-size: 0.875rem;
          font-family: inherit;
          font-weight: 500;
          color: var(--lg-slate-900);
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 0.625rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          appearance: none;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input::placeholder { color: var(--lg-slate-400); font-weight: 400; }

        .lg-input:focus {
          border-color: var(--lg-teal);
          box-shadow: 0 0 0 3px var(--lg-teal-focus), 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-input-wrap:focus-within .lg-input-icon { color: var(--lg-teal); }

        .lg-input.has-error {
          border-color: var(--lg-danger);
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .lg-input-pw { padding-right: 2.875rem; }

        .lg-pw-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: var(--lg-slate-400);
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.375rem;
          transition: color 0.2s;
          line-height: 0;
        }

        .lg-pw-toggle:hover { color: var(--lg-slate-700); }

        .lg-field-err {
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-size: 0.75rem;
          color: var(--lg-danger);
          margin-top: 0.375rem;
          font-weight: 500;
        }

        .lg-forgot {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--lg-teal);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          transition: opacity 0.2s;
          font-family: inherit;
        }

        .lg-forgot:hover { opacity: 0.7; }

        /* ── SELECT ──────────────────────────────────────────────────── */
        .lg-select {
          width: 100%;
          padding: 0.6875rem 0.875rem;
          font-size: 0.875rem;
          font-family: inherit;
          font-weight: 500;
          color: var(--lg-slate-900);
          background: #ffffff;
          border: 1.5px solid #cbd5e1;
          border-radius: 0.625rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          cursor: pointer;
          margin-bottom: 0.875rem;
          appearance: none;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .lg-select:focus {
          border-color: var(--lg-teal);
          box-shadow: 0 0 0 3px var(--lg-teal-focus);
        }

        /* ── ERROR ALERT ─────────────────────────────────────────────── */
        .lg-error-alert {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          background: var(--lg-danger-bg);
          border: 1.5px solid var(--lg-danger-border);
          border-radius: 0.625rem;
          padding: 0.75rem 1rem;
          margin-top: 1rem;
          margin-bottom: 0.25rem;
          color: #dc2626;
          font-size: 0.8125rem;
          font-weight: 500;
          line-height: 1.45;
        }

        .lg-error-icon { flex-shrink: 0; margin-top: 1px; }

        /* ── SUBMIT BUTTON ───────────────────────────────────────────── */
        .lg-submit {
          width: 100%;
          padding: 0.8125rem 1.25rem;
          font-size: 0.9375rem;
          font-weight: 700;
          font-family: inherit;
          letter-spacing: 0.01em;
          border: none;
          border-radius: 0.625rem;
          background: var(--lg-teal);
          color: var(--lg-dark);
          cursor: pointer;
          transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1.375rem;
          position: relative;
          overflow: hidden;
        }

        .lg-submit::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.12) 0%, transparent 60%);
          border-radius: inherit;
          pointer-events: none;
        }

        .lg-submit:hover:not(:disabled) {
          background: #0d9488;
          box-shadow: 0 8px 28px rgba(20, 184, 166, 0.4);
          transform: translateY(-1px);
        }

        .lg-submit:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
        }

        .lg-submit:disabled {
          background: var(--lg-slate-200);
          color: var(--lg-slate-400);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .lg-submit:disabled::after { display: none; }

        .lg-spinner {
          width: 17px;
          height: 17px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: lg-spin 0.75s linear infinite;
          flex-shrink: 0;
        }

        /* ── TENANT SECTION ──────────────────────────────────────────── */
        .lg-tenant-section {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--lg-slate-200);
        }

        .lg-back-btn {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--lg-teal);
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          margin-top: 0.25rem;
          font-family: inherit;
          transition: opacity 0.2s;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .lg-back-btn:hover { opacity: 0.7; }

        /* ── FORM FOOTER ─────────────────────────────────────────────── */
        .lg-form-footer {
          margin-top: 2rem;
          padding-top: 1.25rem;
          border-top: 1px solid var(--lg-slate-100);
        }

        .lg-footer-links {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 1rem;
          margin-bottom: 0.75rem;
          flex-wrap: wrap;
        }

        .lg-footer-link {
          font-size: 0.75rem;
          color: var(--lg-slate-400);
          text-decoration: none;
          font-weight: 500;
          transition: color 0.2s;
        }

        .lg-footer-link:hover { color: var(--lg-teal); }

        .lg-footer-link-website { color: var(--lg-teal); font-weight: 600; }
        .lg-footer-link-website:hover { color: #0d9488; }

        .lg-copyright {
          text-align: center;
          font-size: 0.6875rem;
          color: var(--lg-slate-400);
          margin: 0;
          font-weight: 400;
        }

        /* ── ANIMATIONS ──────────────────────────────────────────────── */
        @keyframes lg-spin { to { transform: rotate(360deg); } }

        @keyframes lg-ring-pulse {
          0%, 100% { opacity: 0.12; }
          50%       { opacity: 0.28; }
        }

        @keyframes lg-flow {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: -32; }
        }

        @keyframes lg-flow-rev {
          from { stroke-dashoffset: 0; }
          to   { stroke-dashoffset: 32; }
        }

        @keyframes lg-enter {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── RESPONSIVE ──────────────────────────────────────────────── */
        /* Below ~1100px the 3-column stats strip can no longer fit each
           value + label on a single line. Stack vertically (value left,
           label right) so each gets the full panel width. */
        @media (max-width: 1100px) {
          .lg-stats { flex-direction: column; }
          .lg-stat-item {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 0.625rem 0.875rem;
            gap: 0.5rem;
          }
          .lg-stat-item + .lg-stat-item {
            border-left: none;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }
          .lg-stat-value {
            font-size: 0.9375rem;
            letter-spacing: -0.015em;
            text-align: left;
            white-space: nowrap;
          }
          .lg-stat-label {
            font-size: 0.5rem;
            letter-spacing: 0.06em;
            text-align: right;
            white-space: nowrap;
          }
        }

        @media (max-width: 900px) {
          .lg-brand { width: 40%; padding: 2rem 2rem; }
          .lg-stats, .lg-features { display: none; }
        }

        @media (max-width: 700px) {
          .lg-root { flex-direction: column; }
          .lg-brand {
            width: 100%;
            flex-direction: row;
            align-items: center;
            padding: 0.875rem 1.375rem;
            flex-shrink: 0;
          }
          .lg-graphic, .lg-stats, .lg-features { display: none; }
          .lg-form-panel { flex: 1; align-items: flex-start; padding: 2rem 1.5rem 2.5rem; }
          .lg-form-inner { max-width: 100%; }
        }

        @media (max-width: 480px) {
          .lg-brand { padding: 0.75rem 1.125rem; }
          .lg-brand-logo-img { height: 50px; }
          .lg-form-panel { padding: 1.5rem 1.25rem 2rem; }
          .lg-form-h1 { font-size: 1.375rem; }
        }

        @media (max-width: 360px) {
          .lg-brand-logo-img { height: 42px; }
        }
      `}</style>

      {/* ── LEFT BRAND PANEL ────────────────────────────────────────── */}
      <aside className="lg-brand">
        <div className="lg-brand-logo">
          <img
            src={`${process.env.PUBLIC_URL}/Logos/syut_logo.png`}
            alt="Syut Technologies"
            className="lg-brand-logo-img"
          />
        </div>

        {/* Abstract node-network graphic */}
        <div className="lg-graphic">
          <svg viewBox="0 0 460 340" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <filter id="lg-glow" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="4" result="blur" />
                <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>
            <line x1="40" y1="40" x2="142" y2="55" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="320" y1="38" x2="428" y2="122" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="428" y1="122" x2="444" y2="178" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="444" y1="178" x2="402" y2="232" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="402" y1="232" x2="300" y2="212" stroke="rgba(148,163,184,0.18)" strokeWidth="1"/>
            <line x1="220" y1="232" x2="234" y2="312" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="234" y1="312" x2="356" y2="284" stroke="rgba(148,163,184,0.16)" strokeWidth="1"/>
            <line x1="62" y1="300" x2="234" y2="312" stroke="rgba(148,163,184,0.14)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="40" y2="40" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="142" y2="55" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="142" y1="55" x2="192" y2="114" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="320" y2="38" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="428" y2="122" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>
            <line x1="192" y1="114" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="252" y2="162" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="356" y2="284" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="300" y1="212" x2="156" y2="264" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="252" y1="162" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="220" y2="232" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="94" y2="180" stroke="rgba(20,184,166,0.26)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="62" y2="300" stroke="rgba(20,184,166,0.2)" strokeWidth="1"/>
            <line x1="156" y1="264" x2="234" y2="312" stroke="rgba(20,184,166,0.18)" strokeWidth="1"/>
            <line x1="82" y1="92" x2="252" y2="162" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-a"/>
            <line x1="252" y1="162" x2="156" y2="264" stroke="rgba(20,184,166,0.55)" strokeWidth="1.5" className="lg-flow-b"/>
            <line x1="378" y1="78" x2="252" y2="162" stroke="rgba(20,184,166,0.45)" strokeWidth="1.5" className="lg-flow-c"/>
            <circle cx="40"  cy="40"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="142" cy="55"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="192" cy="114" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="320" cy="38"  r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="428" cy="122" r="3"   fill="rgba(148,163,184,0.4)"/>
            <circle cx="300" cy="212" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="402" cy="232" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="94"  cy="180" r="3"   fill="rgba(148,163,184,0.45)"/>
            <circle cx="220" cy="232" r="3.5" fill="rgba(148,163,184,0.45)"/>
            <circle cx="62"  cy="300" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="234" cy="312" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="356" cy="284" r="3"   fill="rgba(148,163,184,0.38)"/>
            <circle cx="444" cy="178" r="2.5" fill="rgba(148,163,184,0.32)"/>
            <circle cx="82"  cy="92"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring"/>
            <circle cx="252" cy="162" r="18" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-2"/>
            <circle cx="378" cy="78"  r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-3"/>
            <circle cx="156" cy="264" r="15" fill="none" stroke="rgba(20,184,166,0.15)" strokeWidth="1.5" className="lg-hub-ring-4"/>
            <circle cx="82"  cy="92"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="252" cy="162" r="7.5" fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="378" cy="78"  r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
            <circle cx="156" cy="264" r="6"   fill="#14b8a6" filter="url(#lg-glow)"/>
          </svg>
        </div>

        {/* Stats strip */}
        <div className="lg-stats">
          <div className="lg-stat-item">
            <span className="lg-stat-value">Firm-grade</span>
            <span className="lg-stat-label">Data Security</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Engagement</span>
            <span className="lg-stat-label">Management</span>
          </div>
          <div className="lg-stat-item">
            <span className="lg-stat-value">Audit-ready</span>
            <span className="lg-stat-label">Financials</span>
          </div>
        </div>

        {/* Feature cards */}
        <div className="lg-features">
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Role-based Access Control</span>
              <span className="lg-feature-desc">Fine-grained permissions per user and team</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Multi-tenant Architecture</span>
              <span className="lg-feature-desc">Isolated, secure data per organization</span>
            </div>
          </div>
          <div className="lg-feature-item">
            <div className="lg-feature-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div className="lg-feature-text">
              <span className="lg-feature-title">Real-time Data Sync</span>
              <span className="lg-feature-desc">Live updates across all connected clients</span>
            </div>
          </div>
        </div>

      </aside>

      {/* ── RIGHT FORM PANEL ────────────────────────────────────────── */}
      <main className="lg-form-panel">
        <div className="lg-form-inner">

          {/* App identity */}
          <div className="lg-app-identity">
            <p className="lg-app-name">{appConfig.appTitle}</p>
            <div className="lg-app-badge">
              <span className="lg-app-badge-dot"/>
              Enterprise Engagement Management Suite
            </div>
          </div>

          <h1 className="lg-form-h1">Welcome back</h1>
          <p className="lg-form-sub">Sign in to your account to continue</p>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>

            {/* User ID */}
            <div className="lg-field">
              <label htmlFor="lg-email" className="lg-label">User ID</label>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <input
                  type="text"
                  id="lg-email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={isPending}
                  placeholder="Enter your user ID"
                  aria-label="User ID"
                  aria-invalid={!!emailError}
                  aria-describedby={emailError ? 'lg-email-err' : undefined}
                  className={`lg-input${emailError ? ' has-error' : ''}`}
                  autoComplete="username"
                />
              </div>
              {emailError && (
                <p id="lg-email-err" className="lg-field-err">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {emailError}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="lg-field" style={{ marginBottom: '0.5rem' }}>
              <div className="lg-label-row">
                <label htmlFor="lg-password" className="lg-label">Password</label>
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => alert('Forgot password functionality - Connect to your password reset system')}
                  className="lg-forgot"
                >
                  Forgot?
                </button>
              </div>
              <div className="lg-input-wrap">
                <span className="lg-input-icon">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="lg-password"
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  onKeyDown={handleKeyDown}
                  disabled={isPending}
                  placeholder="Enter your password"
                  aria-label="Password"
                  aria-invalid={!!passwordError}
                  aria-describedby={passwordError ? 'lg-pw-err' : undefined}
                  className={`lg-input lg-input-pw${passwordError ? ' has-error' : ''}`}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="lg-pw-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && (
                <p id="lg-pw-err" className="lg-field-err">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="10" opacity="0.2"/>
                    <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {passwordError}
                </p>
              )}
            </div>

            {/* Tenant selection — tenant-hub mode step 2.
                The application is fixed to the configured PRIMA app
                (REACT_APP_APPLICATION_ID); only the tenant is selectable when
                the user can access PRIMA in more than one tenant. */}
            {getAuthMode() === 'tenant-hub' && authStep === 'tenant-select' && (
              <div className="lg-tenant-section">
                <div className="lg-field">
                  <label className="lg-label">Select Tenant</label>
                  <select
                    value={selectedTenantId}
                    onChange={(e) => setSelectedTenantId(e.target.value)}
                    className="lg-select"
                  >
                    {accessibleTenants.map(t => (
                      <option key={t.tenantId} value={t.tenantId}>
                        {t.tenantDisplayName || t.tenantName} ({t.tenantCode})
                      </option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setAuthStep('credentials');
                    setAccessibleTenants([]);
                    setSelectedTenantId('');
                    setSelectedAppId('');
                    setError('');
                  }}
                  className="lg-back-btn"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                  Back to credentials
                </button>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="lg-error-alert">
                <svg className="lg-error-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="12" cy="12" r="10" opacity="0.2"/>
                  <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            {/* Sign In button */}
            <button
              type="submit"
              disabled={isPending || !!emailError || !!passwordError}
              className="lg-submit"
            >
              {isPending ? (
                <>
                  <div className="lg-spinner"/>
                  Signing in…
                </>
              ) : (
                <>
                  Sign In
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"/>
                    <polyline points="12 5 19 12 12 19"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="lg-form-footer">
            <div className="lg-footer-links">
              <a href="#privacy" className="lg-footer-link">Privacy Policy</a>
              <a href="#terms" className="lg-footer-link">Terms of Service</a>
              <a href="#support" className="lg-footer-link">Support</a>
              <a href="https://syuttechnologies.com" target="_blank" rel="noopener noreferrer" className="lg-footer-link lg-footer-link-website">
                syuttechnologies.com
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginLeft:'3px',verticalAlign:'middle'}}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            </div>
            <p className="lg-copyright">© {new Date().getFullYear()} Syut Technologies. All rights reserved.</p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default LoginInterface;
